import moment from 'moment'
import * as MTZ from 'moment-timezone'
import {
  defaultPaymentCardTypes,
  defaultVehicleCategories,
  defaultCurrencies,
} from '../constants'
import { connectedApiService as api } from '../index'
import { logger } from '../actions'
import _ from 'lodash'
const apiHost = process.env.REACT_APP_APIHOST

const readFileAsync = file => {
  return new Promise((resolve, reject) => {
    let reader = new FileReader()

    reader.onload = e => {
      const binary = window.btoa(e.target.result)
      resolve(`data:${file.type};base64,${binary}`)
    }

    reader.onerror = reject

    reader.readAsBinaryString(file)
  })
}

export const fileToBase64 = async file => {
  let base64 = ''
  try {
    if (file && file.size) {
      base64 = await readFileAsync(file)
    }
  } catch (error) {
    logger({ fileName: 'helpers-index', error: error })
    console.log(error)
  } finally {
    return base64
  }
}
// Function to determine the color based on the percentage
export const getMarginRateColor = percentage => {
  if (percentage >= 75) return 'margin-dot-blue'
  if (percentage >= 25) return 'margin-dot-green'
  if (percentage >= 0) return 'margin-dot-orange'
  return 'margin-dot-red'
}

export const getMarginLabelClass = percentage => {
  if (percentage >= 75) return 'margin-label-blue'
  if (percentage >= 25) return 'margin-label-green'
  if (percentage >= 0) return 'margin-label-orange'
  return 'margin-label-red'
}

export const getMarginColor = percentage => {
  if (percentage >= 75) return '#3140B0'
  if (percentage >= 25) return '#417505'
  if (percentage >= 0) return '#FFA500'
  return '#D0021B'
}
export const getPickupInstructionOptions = ({ isTrain, isPrivate } = {}) => {
  if (isTrain) {
    return [
      { label: 'Train platform', value: 'Train platform' },
      { label: 'Other', value: 'Other', type: 'other' },
    ]
  }
  if (isPrivate) {
    return [
      { label: 'Tarmac by the aircraft', value: 'Tarmac by the aircraft' },
      { label: 'Private terminal', value: 'Private terminal' },
      { label: 'Other', value: 'Other', type: 'other' },
    ]
  }
  return [
    { label: 'Arrivals hall M&G', value: 'Arrivals hall M&G' },
    { label: 'Curbside pick-up', value: 'Curbside pick-up' },
    { label: 'Other', value: 'Other', type: 'other' },
  ]
}

export const getOptionFromValue = (defaultData = [], val) =>
  defaultData.find(item => item.value === val) || {}

export const getOptionFromLabel = (defaultData = [], val) =>
  defaultData.find(item => item.label === val) || {}
export const getLabelFromValue = (defaultData = [], val) =>
  getOptionFromValue(defaultData, val).label || val

/**
 * Converts an internal currency key (e.g. "currency_eur") to its ISO display code ("EUR").
 * Falls back to the raw value if no match is found.
 */
export const getCurrencyLabel = value =>
  defaultCurrencies.find(c => c.value === value)?.label || value || ''

export const getValueFromLabel = (defaultData = [], val) =>
  getOptionFromLabel(defaultData, val).value || val
export const getLabelsFromArrayOfValues = (defaultData = [], values = []) =>
  (values || []).map(val => getLabelFromValue(defaultData, val))

export const getLabelFromCode = (defaultData = [], val) =>
  defaultData.find(item => item.code === val) || {}

export const getAllMatchingOptionsFromArrValue = (defaultData = [], values) =>
  defaultData.filter(item => values.includes(item.value))

export const getCategoryLabelFromValue = val => {
  return getOptionFromValue(defaultVehicleCategories, val).label || val
}
export const getPaymentCardIcon = cardType =>
  (
    defaultPaymentCardTypes.find(
      ({ type, altType }) => cardType === type || cardType === altType
    ) || {}
  ).icon || 'placeholder.svg'

export const getDefaultPaymentCredentialsId = (payments = []) => {
  if (!payments || !payments.length) {
    return null
  }
  return (
    payments.find(({ isDefaultPaymentMethod }) => !!isDefaultPaymentMethod) ||
    {}
  ).id
}

export const getRoundedTime = (time, roundMinutes = 15) => {
  const start = moment(time)
  const remainder = roundMinutes - (start.minute() % roundMinutes)
  return moment(start).add(remainder, 'minutes')
}

export const getFormattedTripInfo = tripInfo => {
  if (!tripInfo) {
    return
  }
  let formattedInfo = {}
  if (!tripInfo.duration) {
    formattedInfo.duration = '0 h 0 min'
  } else {
    const parsedDuration = moment.utc(tripInfo.duration * 1000).format('H:mm')
    const splittedValue = parsedDuration.split(':')
    formattedInfo.duration = `${
      splittedValue[0] !== '0'
        ? `${splittedValue[0]} h ${splittedValue[1]} min`
        : `${splittedValue[1]} min`
    }`
  }

  if (!tripInfo.distance) {
    formattedInfo.distance = tripInfo.unitSystem === 'KM' ? '0 km' : '0 miles'
  } else {
    let value =
      tripInfo && tripInfo.unitSystem === 'KM'
        ? tripInfo.distance / 1000
        : (tripInfo.distance / 1000) * 0.621371
    formattedInfo.distance = `${Math.round(value).toFixed(2)} ${
      tripInfo && tripInfo.unitSystem === 'KM' ? 'km' : 'miles'
    }`
  }
  return formattedInfo
}

export const getFormattedPrice = price => {
  if (!price) {
    return '0.00'
  }

  return `${parseFloat(price).toFixed(2)}`
}

/**
 * True only when there is definitively nothing to pay.
 *
 * A 100% promo code (or a price set to zero) leaves a payable amount of 0, and the
 * "your card will be charged 0.00 ... or the service will be automatically cancelled"
 * banners then threaten the client over a payment that will never be taken. Callers
 * use this to hide those banners rather than announce a zero charge.
 *
 * Tolerates the two shapes these props arrive in: a raw number, or an already
 * formatted string like "1,234.00". Anything that is not a number is treated as
 * UNKNOWN and returns false, so the banner still shows whatever it showed before -
 * note "n/a" strips to "" and Number('') is 0, which would otherwise hide it.
 */
export const isNothingToPay = value => {
  if (value === null || value === undefined || value === '') return false
  const cleaned = String(value).replace(/[^0-9.-]/g, '')
  if (!/\d/.test(cleaned)) return false
  const parsed = Number(cleaned)
  return Number.isFinite(parsed) && parsed === 0
}

/**
 * True when a promotion code works as a DISCOUNT on the client price. The two
 * "discount/commission" types are a discount only for a client billed on discount
 * (benefits.discountWeight > 0); on commission billing they leave the price as it is
 * and pay commission instead. Mirrors rolzo-api isDiscountPromoCode.
 */
export const isDiscountPromoCode = (promoCode, benefits) => {
  if (!promoCode || !promoCode.type) return false
  if (
    ['fixedDiscount', 'percentageDiscount', 'discountCommissionSplit'].includes(
      promoCode.type
    )
  )
    return true
  return (
    ['fixedDiscComm', 'percentDiscComm'].includes(promoCode.type) &&
    benefits?.discountWeight > 0
  )
}

/**
 * True when the discount promotion code can itself bring the price to 0: a fixed amount (as large as the
 * price) or a 100% discount. With any other code a price of 0 was set by hand.
 */
export const canPromoCodeMakePriceZero = (promoCode, benefits) =>
  isDiscountPromoCode(promoCode, benefits) &&
  (['fixedDiscount', 'fixedDiscComm'].includes(promoCode.type) ||
    parseFloat(promoCode.discountWeight) === 100 ||
    parseFloat(benefits?.discountWeight) === 100)

/**
 * A discount promotion code leaves the client nothing to pay (`total` is everything
 * the booking would charge), so the booking is made without a payment method. Under a
 * cent, or below zero for a fixed code larger than the price, counts as nothing; an
 * unknown total does not. The API re-checks the code and the amounts.
 */
export const isFreeByDiscountPromo = (promoCode, benefits, total) => {
  const amount = parseFloat(total)
  return (
    !!promoCode?.code &&
    isDiscountPromoCode(promoCode, benefits) &&
    Number.isFinite(amount) &&
    amount < 0.005
  )
}

/**
 * Copy for the "change the payment status" confirmation.
 *
 * Non-billable gets its own sentence because it is the only status that silently
 * stops the payment automation: nobody should pick it expecting the booking to
 * still be charged. Every other status keeps the generic wording.
 */
export const getPaymentStatusConfirmationText = status =>
  status === 'non_billable'
    ? 'Are you sure you want to change the payment status to Non-billable? Payment for this booking will not be attempted.'
    : `Are you sure you want to mark this payment as ${status}?`

export const numberWithCommas = price => {
  var parts = price.toString().split('.')
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return parts.join('.')
}

export const formatSentenceCase = str => {
  if (!str) return ''
  return str.replace(/\w\S*/g, function(txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  })
}

export const isAndroid = () => {
  var userAgent = navigator.userAgent || navigator.vendor || window.opera
  if (/android/i.test(userAgent)) {
    return true
  } else {
    return false
  }
}
export const isIos = () => {
  var userAgent = navigator.userAgent || navigator.vendor || window.opera
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return true
  } else {
    return false
  }
}
export const isMobile = () => {
  var userAgent = navigator.userAgent || navigator.vendor || window.opera
  // Windows Phone must come first because its UA also contains "Android"
  if (/windows phone/i.test(userAgent)) {
    return true
  }

  if (/android/i.test(userAgent)) {
    return true
  }

  // iOS detection
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return true
  }

  return false
}

export const hideResponsiveButton = () => {
  if (
    !isMobile() ||
    document.getElementsByClassName('book-continue-btn') === null
  ) {
    return
  }

  var continueBtns = document.getElementsByClassName('book-continue-btn')
  for (var i = 0; i < continueBtns.length; i++) {
    continueBtns[i].classList.add('hide-bottom-button')
  }
}

export const showResponsiveButton = () => {
  if (
    !isMobile() ||
    document.getElementsByClassName('book-continue-btn') === null
  ) {
    return
  }

  var continueBtns = document.getElementsByClassName('book-continue-btn')
  for (var i = 0; i < continueBtns.length; i++) {
    continueBtns[i].classList.remove('hide-bottom-button')
  }
}

export const requestFullscreen = element => {
  if (element.requestFullscreen) {
    element.requestFullscreen()
  } else if (element.webkitRequestFullScreen) {
    element.webkitRequestFullScreen()
  } else if (element.mozRequestFullScreen) {
    element.mozRequestFullScreen()
  } else if (element.msRequestFullScreen) {
    element.msRequestFullScreen()
  }
}

export const getMeetAssistMeesage = message => {
  let separateLines = message.split(/\r?\n|\r|\n/g)
  return separateLines
}

// Formats vipPassenger age: infants store months (0 = less than a month), children/adults store years.
// Handles both raw numbers and saved option objects { label, value }.
export const formatPassengerAge = (age, passengerType) => {
  if (age == null || age === '') return null
  const ageValue = typeof age === 'object' ? age.value : age
  if ((passengerType || '').toLowerCase() === 'infant') {
    if (ageValue === 0) return 'Less than a month'
    return `${ageValue} month${ageValue === 1 ? '' : 's'}`
  }
  return typeof age === 'object'
    ? age.label || String(ageValue)
    : String(ageValue)
}

export const formatChargesText = (text, desktop, type) => {
  //show full text for vip charges
  if (type === 'arrival' || type === 'departure') {
    if (text.length > 21) {
      return text.substring(0, 21) + '...'
    }
    return text
  }

  //conditions for other charges
  if (text.includes(',')) {
    const [vehicleName, waitingTime] = text.split(',')

    if (waitingTime.length > 21 && desktop) {
      return waitingTime.substring(0, 21) + '...'
    }
    return waitingTime
  }

  if (text.length > 21) {
    return text.substring(0, 21) + '...'
  }
  return text
}

export const getEffectiveMinHours = (minHours, stops) => {
  let effectiveMinHours = minHours

  if (Array.isArray(stops) && stops.length) {
    const surgeMinHours = Math.max(
      0,
      ...stops
        .filter(
          s =>
            s.durationType === 'hourly' &&
            s.surgeEvent &&
            s.surgeMinHours &&
            s.surgeMinHours > 0
        )
        .map(s => s.surgeMinHours)
    )

    if (surgeMinHours && (!minHours || surgeMinHours > minHours)) {
      effectiveMinHours = surgeMinHours
    }
  }

  return effectiveMinHours
}

//cancellation policy functions
export const kilometersToMiles = kilometers => {
  return kilometers / 1.60934
}
export const toRadius = angle => {
  return (angle / 180) * Math.PI
}
export const getDistance = (latitude1, longitude1, latitude2, longitude2) => {
  let earth_radius = 6371
  let dLat = toRadius(latitude2 - latitude1)
  let dLon = toRadius(longitude2 - longitude1)
  let a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadius(latitude1)) *
      Math.cos(toRadius(latitude2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2)
  let c = 2 * Math.asin(Math.sqrt(a))
  let d = earth_radius * c
  return kilometersToMiles(d)
}

export const isMoreThanTwoHours = tripDuration => {
  const duration = moment.duration(tripDuration, 'seconds')
  const numHours = duration.hours()
  const numMinutes = duration.minutes()

  if (numHours > 2 || (numHours === 2 && numMinutes > 0)) {
    return true
  } else {
    return false
  }
}

export const findCityInGroups = (pickUpLocation, cityGroups) => {
  if (!Array.isArray(cityGroups)) {
    return null
  }
  for (const group of cityGroups) {
    for (const item of group.cities) {
      let distance = getDistance(
        item.city.lat,
        item.city.lng,
        pickUpLocation.lat,
        pickUpLocation.lng
      )

      if (distance <= item.radius) {
        return group
      }
    }
  }
  return null
}

export const findCountryInGroups = (pickUpLocation, countryGroups) => {
  if (!Array.isArray(countryGroups)) {
    return null
  }
  for (const group of countryGroups) {
    const foundCountry = group.countries.find(
      item => item.country === pickUpLocation.country
    )
    if (foundCountry) {
      return group
    }
  }
  return null
}

// {globalPolicies.globalCitiesPolicy &&
//   globalPolicies.globalCitiesPolicy.some(
//     item =>
//       item.city ===
//       values.pickUpLocation.city
//   ) &&
//   globalPolicies.citiesTransferPolicy ? (
//     <p>{`${globalPolicies.citiesTransferPolicy} before pick-up`}</p>
//   ) : globalPolicies.globalTransferPolicy ? (
//     <p>{`${globalPolicies.globalTransferPolicy} before pick-up`}</p>
//   ) : (
//     <>
//       {/* Policies for certain vehicles */}
//       {vehicleTypesPolicy.includes(
//         info.vehicleCategory
//       ) ? (
//         <>
//           <p>48 hours before pick-up</p>
//         </>
//       ) : (
//         <>
//           {info.vehicle
//             .categoryChauffeured ===
//             'van' ||
//           values.pickUpLocation.country ===
//             'JP' ? (
//             <p>24 hours before pick-up</p>
//           ) : (
//             <p>4 hours before pick-up</p>
//           )}
//         </>
//       )}
//     </>
//   )}
export const getGlobalCancellationPolicy = values => {
  try {
    const {
      bookingType,
      pickUpLocation,
      durationType,
      globalPolicy,
      vehicleCategory,
    } = values

    if (bookingType === 'airport_transfer') {
      const vehicleTypesPolicy = ['coach', 'luxe', 'minibus', 'stretch']

      let cityPolicy = findCityInGroups(
        pickUpLocation,
        globalPolicy?.citiesGroup
      )
      let countryPolicy = findCountryInGroups(
        pickUpLocation,
        globalPolicy?.countriesGroup
      )

      if (cityPolicy && cityPolicy.transferPolicy) {
        return cityPolicy.transferPolicy
      } else if (countryPolicy && countryPolicy.transferPolicy) {
        return countryPolicy.transferPolicy
      } else {
        //default cancellation policy
        return vehicleTypesPolicy.includes(vehicleCategory)
          ? '48 hours'
          : vehicleCategory === 'van'
          ? '24 hours'
          : '4 hours'
      }
    } else if (bookingType === 'chauffeur_services') {
      const vehicleTypesPolicy = ['coach', 'luxe', 'minibus', 'stretch']

      if (durationType === 'daily') {
        //Global policies
        let cityPolicy = findCityInGroups(
          pickUpLocation,
          globalPolicy?.citiesGroup
        )
        let countryPolicy = findCountryInGroups(
          pickUpLocation,
          globalPolicy?.countriesGroup
        )

        if (cityPolicy && cityPolicy.dailyPolicy) {
          return cityPolicy.dailyPolicy
        } else if (countryPolicy && countryPolicy.dailyPolicy) {
          return countryPolicy.dailyPolicy
        } else {
          //default cancellation policy
          return vehicleTypesPolicy.includes(vehicleCategory)
            ? '48 hours'
            : '24 hours'
        }
      } else if (durationType === 'hourly') {
        let cityPolicy = findCityInGroups(
          pickUpLocation,
          globalPolicy?.citiesGroup
        )
        let countryPolicy = findCountryInGroups(
          pickUpLocation,
          globalPolicy?.countriesGroup
        )

        if (cityPolicy && cityPolicy.hourlyPolicy) {
          return cityPolicy.hourlyPolicy
        } else if (countryPolicy && countryPolicy.hourlyPolicy) {
          return countryPolicy.hourlyPolicy
        } else {
          //default cancellation policy
          return vehicleTypesPolicy.includes(vehicleCategory)
            ? '48 hours'
            : '24 hours'
        }
      }
    } else if (bookingType === 'car_rentals') {
      let cityPolicy = findCityInGroups(
        pickUpLocation,
        globalPolicy?.citiesGroup
      )
      let countryPolicy = findCountryInGroups(
        pickUpLocation,
        globalPolicy?.countriesGroup
      )

      if (cityPolicy && cityPolicy.carRentalPolicy) {
        return cityPolicy.carRentalPolicy
      } else if (countryPolicy && countryPolicy.carRentalPolicy) {
        return countryPolicy.carRentalPolicy
      } else {
        //default cancellation policy
        return '48 hours'
      }
    }
  } catch (error) {
    console.log('Get cancellation policy', error)
    logger.error('Get cancellation policy', error)
  }
}

export const getVipMeetGreetCancellation = values => {
  try {
    const { event, pickUpLocation, globalPolicy } = values

    //Check events cancellation policy
    if (event && event.meetAndGreetPolicy) {
      if (!isInvalidCancellationToken(event.meetAndGreetPolicy)) {
        return event.meetAndGreetPolicy
      }
    }

    //Check cities cancellation policy
    let cityPolicy = findCityInGroups(pickUpLocation, globalPolicy?.citiesGroup)
    if (cityPolicy && cityPolicy.meetAndGreetPolicy) {
      return cityPolicy.meetAndGreetPolicy
    }

    //Check countries cancellation policy
    let countryPolicy = findCountryInGroups(
      pickUpLocation,
      globalPolicy?.countriesGroup
    )
    if (countryPolicy && countryPolicy.meetAndGreetPolicy) {
      return countryPolicy.meetAndGreetPolicy
    }

    //default cancellation policy
    return '48 hours'
  } catch (error) {
    console.log('Get cancellation policy Terms', error)
  }
}

export const isInvalidCancellationToken = value => {
  if (value == null || value === '') return true
  const str = String(value).trim()
  return /^[a-zA-Z]$/.test(str)
}

export const isStaleVipCancellationToken = value => {
  if (value == null || value === '') return true
  return /\b99\b/.test(String(value).trim())
}

export const formatCancellationForDisplay = raw => {
  if (isInvalidCancellationToken(raw) || isStaleVipCancellationToken(raw))
    return null
  const str = String(raw).trim()
  if (/[a-zA-Z]/.test(str)) {
    return str.replace(/hours?/gi, 'hrs')
  }
  return `${str} hrs`
}

/** Convert a cancellation duration ("7 days", "48 hours", "48", 48) to hours. */
export const cancellationPolicyToHours = raw => {
  if (isInvalidCancellationToken(raw) || isStaleVipCancellationToken(raw))
    return null
  const str = String(raw).trim()
  const match = str.match(
    /^(\d+(?:\.\d+)?)\s*(minutes?|hours?|hrs?|days?|weeks?)?$/i
  )
  if (!match) {
    const fallback = str.match(/\d+(\.\d+)?/)
    return fallback ? Number(fallback[0]) : null
  }
  const value = Number(match[1])
  const unit = (match[2] || 'hours').toLowerCase()
  if (unit.startsWith('minute')) return value / 60
  if (unit.startsWith('day')) return value * 24
  if (unit.startsWith('week')) return value * 24 * 7
  return value // hours / hrs / bare number
}

export const getMeetAssistLegSellingRatePerHour = leg => {
  if (!leg) return 0
  const rate =
    leg.sellingRatePerHour ??
    leg.selling_rate_per_hour ??
    leg.vipRate?.sellingRatePerHour ??
    leg.vipRate?.selling_rate_per_hour
  const n = parseFloat(rate)
  return n > 0 ? n : 0
}

export const getMeetAssistLegHoursIncluded = leg => {
  if (!leg) return 0
  const raw =
    leg.hours_included ??
    leg.hoursIncluded ??
    leg.vipRate?.hours_included ??
    leg.vipRate?.hoursIncluded
  const n = parseFloat(raw)
  return n > 0 ? n : 0
}

const normalizeCancellationRootFallback = (rootFallback = null) => {
  if (rootFallback == null) return null
  if (typeof rootFallback === 'object') {
    return rootFallback.cancellation ?? null
  }
  return rootFallback
}

const getMeetAssistLegCancellationCandidates = (leg, rootFallback = null) => [
  leg?.cancellation_policy,
  leg?.cancellationPolicy?.cancellation,
  typeof leg?.cancellationPolicy === 'string' ? leg?.cancellationPolicy : null,
  normalizeCancellationRootFallback(rootFallback),
]

export const getMeetAssistLegCancellationDisplay = (
  leg,
  rootFallback = null
) => {
  if (!leg && !rootFallback) return null

  const candidates = getMeetAssistLegCancellationCandidates(leg, rootFallback)

  for (const raw of candidates) {
    const formatted = formatCancellationForDisplay(raw)
    if (formatted) return formatted
  }

  return null
}

// Raw cancellation-window size in hours (e.g. "48 hours" / "7 days" / 48 -> hours).
// Used for the timezone-aware "within cancellation window" check, kept separate
// from getMeetAssistLegCancellationDisplay's formatted-text output.
export const getMeetAssistLegCancellationHours = (leg, rootFallback = null) => {
  if (!leg && !rootFallback) return null

  const candidates = getMeetAssistLegCancellationCandidates(leg, rootFallback)

  for (const raw of candidates) {
    const hours = cancellationPolicyToHours(raw)
    if (hours != null && hours > 0) return hours
  }

  return null
}

// Timezone-aware "is this leg now within its cancellation window" check.
// serviceDateTime is the leg's own arrival/departure time (NOT the transfer's
// pickUpDate). It is already stored as the exact local date/time at the
// pick-up location (not a true UTC instant) -- so we read its digits as-is
// (no reinterpretation) and compare them against the current wall-clock time
// at that same location, entirely in "local wall clock" terms. This makes the
// result correct regardless of what timezone the browser/server happens to
// be running in.
export const getMeetAssistLegCancellationWarning = ({
  leg,
  rootFallback = null,
  serviceDateTime = null,
  timeZoneId = null,
} = {}) => {
  if (!serviceDateTime) return false

  const cancellationHours = getMeetAssistLegCancellationHours(leg, rootFallback)
  if (cancellationHours == null) return false

  const LOCAL_FORMAT = 'YYYY-MM-DDTHH:mm:ss'
  const eventLocal = MTZ.utc(serviceDateTime).format(LOCAL_FORMAT)
  const nowLocal = timeZoneId
    ? MTZ.tz(timeZoneId).format(LOCAL_FORMAT)
    : MTZ.utc().format(LOCAL_FORMAT)

  const diffHours = moment(eventLocal).diff(moment(nowLocal), 'hours', true)
  return diffHours <= cancellationHours
}

/**
 * Build per-leg VIP M&G terms for TandCCard when arrival and/or departure add-ons exist.
 * Pass meetAndAssist (booking details) or raw arrival/departure (confirm flow).
 */
export const buildAddonVipMgTermsLegs = ({
  meetAndAssist = null,
  arrival = null,
  departure = null,
  rootCancellationPolicy = null,
  overridePolicy = null, // event/banner meetAndGreetPolicy — overwrites rate card
  isLegActive = () => true,
  timeZoneId = null,
  computeCancellationWarning = true,
} = {}) => {
  const arr = arrival ?? meetAndAssist?.arrival
  const dep = departure ?? meetAndAssist?.departure
  const rootFallback =
    rootCancellationPolicy ?? meetAndAssist?.cancellationPolicy ?? null

  // Active event/banner override wins over the rate card (matches the
  // "Fill-in to overwrite default cancellation policies" event behaviour).
  // Accept either a string ("7 days") or a { cancellation } object.
  const rawOverride =
    overridePolicy && typeof overridePolicy === 'object'
      ? overridePolicy.cancellation
      : overridePolicy
  const override =
    rawOverride != null &&
    !isInvalidCancellationToken(rawOverride) &&
    !isStaleVipCancellationToken(rawOverride)
      ? rawOverride
      : null
  const overrideDisplay = override
    ? formatCancellationForDisplay(override)
    : null

  const legs = []
  const pushLeg = (leg, title, id, serviceDateTime) => {
    if (!leg || !isLegActive(leg)) return
    legs.push({
      id,
      title,
      cancellationPolicy:
        overrideDisplay ||
        getMeetAssistLegCancellationDisplay(leg, rootFallback),
      hoursIncluded: getMeetAssistLegHoursIncluded(leg),
      sellingRatePerHour: getMeetAssistLegSellingRatePerHour(leg),
      showCancellationWarning: computeCancellationWarning
        ? getMeetAssistLegCancellationWarning({
            leg: override ? null : leg,
            rootFallback: override || rootFallback,
            serviceDateTime,
            timeZoneId,
          })
        : false,
    })
  }

  pushLeg(arr, 'VIP Meet & Greet, Arrival', 'arrival', arr?.arrivalTime)
  pushLeg(dep, 'VIP Meet & Greet, Departure', 'departure', dep?.departureTime)

  return legs
}

export const getAddonMeetAssistSellingRatePerHour = meetAndAssist => {
  if (!meetAndAssist) return 0
  return (
    getMeetAssistLegSellingRatePerHour(meetAndAssist.arrival) ||
    getMeetAssistLegSellingRatePerHour(meetAndAssist.departure)
  )
}

export const getAddonMeetAssistCancellationDisplay = meetAndAssist => {
  if (!meetAndAssist) return null

  // M&A root cancellationPolicy is the source of truth (carries any event
  // override and is what refund/charge logic uses). Prefer it over each leg's
  // stale rate-card snapshot. Accepts a string ("7 days") or { cancellation }.
  const rootRaw =
    meetAndAssist.cancellationPolicy &&
    typeof meetAndAssist.cancellationPolicy === 'object'
      ? meetAndAssist.cancellationPolicy.cancellation
      : meetAndAssist.cancellationPolicy
  if (!isStaleVipCancellationToken(rootRaw)) {
    const rootDisplay = formatCancellationForDisplay(rootRaw)
    if (rootDisplay) return rootDisplay
  }

  const service = meetAndAssist.arrival || meetAndAssist.departure
  return getMeetAssistLegCancellationDisplay(
    service,
    meetAndAssist.cancellationPolicy
  )
}
export const getGlobalCancellationPolicyTerms = values => {
  try {
    const {
      bookingType,
      pickUpLocation,
      durationType,
      globalPolicy,
      vehicleCategory,
    } = values

    if (bookingType === 'airport_transfer') {
      const vehicleTypesPolicy = ['coach', 'luxe', 'minibus', 'stretch']

      let cityPolicy = findCityInGroups(
        pickUpLocation,
        globalPolicy?.citiesGroup
      )
      let countryPolicy = findCountryInGroups(
        pickUpLocation,
        globalPolicy?.countriesGroup
      )

      if (cityPolicy && cityPolicy.transferPolicy) {
        return cityPolicy.transferPolicy
      } else if (countryPolicy && countryPolicy.transferPolicy) {
        return countryPolicy.transferPolicy
      } else {
        //default cancellation policy
        return vehicleTypesPolicy.includes(vehicleCategory)
          ? '48 hrs'
          : vehicleCategory === 'van'
          ? '24 hrs'
          : '4 hrs'
      }
    } else if (bookingType === 'chauffeur_services') {
      const vehicleTypesPolicy = ['coach', 'luxe', 'minibus', 'stretch']

      if (durationType === 'daily') {
        //Global policies
        let cityPolicy = findCityInGroups(
          pickUpLocation,
          globalPolicy?.citiesGroup
        )
        let countryPolicy = findCountryInGroups(
          pickUpLocation,
          globalPolicy?.countriesGroup
        )

        if (cityPolicy && cityPolicy.dailyPolicy) {
          return cityPolicy.dailyPolicy
        } else if (countryPolicy && countryPolicy.dailyPolicy) {
          return countryPolicy.dailyPolicy
        } else {
          //default cancellation policy
          return vehicleTypesPolicy.includes(vehicleCategory)
            ? '48 hrs'
            : '24 hrs'
        }
      } else if (durationType === 'hourly') {
        let cityPolicy = findCityInGroups(
          pickUpLocation,
          globalPolicy?.citiesGroup
        )
        let countryPolicy = findCountryInGroups(
          pickUpLocation,
          globalPolicy?.countriesGroup
        )

        if (cityPolicy && cityPolicy.hourlyPolicy) {
          return cityPolicy.hourlyPolicy
        } else if (countryPolicy && countryPolicy.hourlyPolicy) {
          return countryPolicy.hourlyPolicy
        } else {
          //default cancellation policy
          return vehicleTypesPolicy.includes(vehicleCategory)
            ? '48 hrs'
            : '24 hrs'
        }
      }
    } else if (bookingType === 'car_rentals') {
      let cityPolicy = findCityInGroups(
        pickUpLocation,
        globalPolicy?.citiesGroup
      )
      let countryPolicy = findCountryInGroups(
        pickUpLocation,
        globalPolicy?.countriesGroup
      )

      if (cityPolicy && cityPolicy.carRentalPolicy) {
        return cityPolicy.carRentalPolicy
      } else if (countryPolicy && countryPolicy.carRentalPolicy) {
        return countryPolicy.carRentalPolicy
      } else {
        //default cancellation policy
        return '48 hrs'
      }
    }
  } catch (error) {
    console.log('Get cancellation policy Terms', error)
    logger.error('Get cancellation policy Terms', error)
  }
}

export const isBeforeCancellationPolicy = (
  pickUpDate,
  pickUpTime,
  cancellationPolicy,
  timeZoneId
) => {
  if (!pickUpDate || !pickUpTime || !cancellationPolicy) return true
  const pickUpMoment = moment(`${pickUpDate} ${pickUpTime}`, 'DD/MM/YYYY HH:mm')
  const hours = cancellationPolicyToHours(cancellationPolicy)
  if (hours == null) return true
  const cancellationDeadline = pickUpMoment.clone().subtract(hours, 'hours')
  const currentTime = timeZoneId ? MTZ.tz(timeZoneId) : moment()
  return currentTime.isBefore(cancellationDeadline)
}

export const triggerNotificationApi = async ({
  title,
  body,
  chatId,
  isBookingChat,
  data,
}) => {
  if (!('Notification' in window)) {
    console.warn('Notifications not supported in this browser')
    return null
  }
  const shouldSkipNotification =
    document.visibilityState === 'visible' && document.hasFocus()
  if (shouldSkipNotification) {
    console.log('Page is visible and has focus - skipping notification')
    return null
  }
  const permission =
    Notification.permission === 'granted'
      ? 'granted'
      : await Notification.requestPermission()

  if (permission !== 'granted') {
    console.warn('Notification permission not granted')
    return null
  }

  let audio = null
  const playSound = () => {
    try {
      if (!audio) {
        audio = new Audio('/audio/rolzo-notification-sound.mp3')
        audio.preload = 'auto'
        audio.volume = 0.5
      } else {
        audio.currentTime = 0
      }
      const playPromise = audio.play()
      if (playPromise !== undefined) {
        playPromise
          .catch(e => {
            console.warn('Sound playback was blocked:', e)
          })
          .finally(() => {
            audio = null
          })
      }
    } catch (err) {
      console.error('Error playing notification sound:', err)
      audio = null
    }
  }

  const showNotification = () => {
    try {
      const notificationOptions = {
        body,
        icon: '/images/Pure white BG.png',
        badge: '/images/Pure white BG.png',
        data: { chatId, isBookingChat },
        tag: `chat-${chatId}`, // Group similar notifications
        // requireInteraction: true,
        silent: false, // We'll handle the sound ourselves
      }

      const notification = new Notification(title, notificationOptions)
      notification.onclick = () => {
        if (window.location.pathname === `/support-admin`) {
          window.focus()
        } else {
          window.open(`/support-admin`, '_self')
        }
        notification.close()
      }

      return notification
    } catch (err) {
      console.error('Error creating notification:', err)
      return null
    }
  }

  const notification = showNotification()
  if (notification && permission === 'granted') {
    setTimeout(playSound, 100)
  }
}
export const getPartnerOriginLink = () => {
  if (apiHost === 'https://staging.rolzo.com/api') {
    return `https://partners-staging.rolzo.com`
  } else if (apiHost === 'https://business.rolzo.com/api') {
    return `https://partners.rolzo.com`
  } else if (apiHost === 'http://localhost:3000') {
    return `http://localhost:3002`
  }
}

const EQUIVALENT_VEHICLE_PAIRS = {
  'Mercedes-Benz E-Class': 'Mercedes-Benz EQE',
  'Mercedes-Benz EQE': 'Mercedes-Benz E-Class',
  'Mercedes-Benz V-Class': 'Mercedes-Benz EQV',
  'Mercedes-Benz EQV': 'Mercedes-Benz V-Class',
}

const VEHICLE_FILTER_ALIAS_GROUPS = [
  ['e-class', 'eqe'],
  ['v-class', 'eqv'],
]

export const expandVehicleFilterQueries = (filter = '') => {
  const normalized = String(filter)
    .trim()
    .toLowerCase()
  if (!normalized) return []

  const queries = new Set([normalized])
  VEHICLE_FILTER_ALIAS_GROUPS.forEach(([a, b]) => {
    if (normalized.includes(a)) queries.add(normalized.split(a).join(b))
    if (normalized.includes(b)) queries.add(normalized.split(b).join(a))
  })

  return [...queries]
}

export const vehicleFilterMatchesText = (text = '', filter = '') => {
  const queries = expandVehicleFilterQueries(filter)
  if (!queries.length) return true

  const label = String(text)
    .trim()
    .toLowerCase()
  return queries.some(query => {
    const tokens = query.split(/\s+/).filter(Boolean)
    return tokens.every(token => label.includes(token))
  })
}

export const partnerHasMatchingVehicle = (vehicles = [], filter = '') => {
  const queries = expandVehicleFilterQueries(filter)
  if (!queries.length) return true
  if (!Array.isArray(vehicles) || !vehicles.length) return false

  return vehicles.some(vehicle =>
    vehicleFilterMatchesText(String(vehicle), filter)
  )
}

export const formatAcceptanceTime = minutes => {
  const totalMins = Math.max(0, Math.round(Number(minutes) || 0))
  if (!totalMins) return '0 m'

  const days = Math.floor(totalMins / (24 * 60))
  const hours = Math.floor((totalMins % (24 * 60)) / 60)
  const mins = totalMins % 60

  const parts = []
  if (days) parts.push(`${days} d`)
  if (hours) parts.push(`${hours} h`)
  if (mins || !parts.length) parts.push(`${mins} m`)
  return parts.join(' ')
}

export const formatEscalationsLabel = (escalations, totalAcceptedBookings) => {
  const count = Number(escalations) || 0
  const accepted = Number(totalAcceptedBookings) || 0
  const percentage =
    accepted > 0 ? ((count / accepted) * 100).toFixed(2) : '0.00'
  return `${count} (${percentage}%) escalation${count !== 1 ? 's' : ''}`
}

export const isVehicleMismatch = (
  globalVehicle,
  partnerGlobalVehicleId,
  partnerVehicle
) => {
  if (!globalVehicle || !partnerGlobalVehicleId) return false
  if (globalVehicle.id === partnerGlobalVehicleId) return false
  const bookedName = `${globalVehicle.brandName ||
    ''} ${globalVehicle.modelName || ''}`.trim()
  if (bookedName === partnerVehicle) return false
  return EQUIVALENT_VEHICLE_PAIRS[bookedName] !== partnerVehicle
}

export const isGreaterThanZero = (actualRate, weight) =>
  (actualRate * weight) / 100 > 0

export const calculateCommissionValue = (
  actualRate,
  commissionWeight,
  discountWeight = 0,
  bookingCountry = null,
  bookingCreatedAt = null
) => {
  if (!actualRate || !commissionWeight) {
    return 0
  }

  let basePrice = parseFloat(actualRate)

  if (discountWeight && discountWeight > 0) {
    basePrice = basePrice - (basePrice * parseFloat(discountWeight)) / 100
  }

  // considering 20% VAT for UK bookings created on/after 1 Aug 2026
  if (
    bookingCountry === 'GB' &&
    (!bookingCreatedAt || new Date(bookingCreatedAt) > new Date('2026-08-01'))
  ) {
    basePrice = basePrice / 1.2
  }

  if (basePrice < 0) {
    basePrice = 0
  }

  const commission = (basePrice * parseFloat(commissionWeight)) / 100

  return parseFloat(commission.toFixed(2))
}

// Standalone VIP only — decompose a CR charge / refund client-facing AMOUNT into its
// pre-markup base and markup, using the EXACT rule the change-request charge was created
// with on the backend (booking.js ~27127-27135 and payouts/helpers decomposeStandaloneVipAmount).
// This guarantees a refund that REVERSES a charge yields the identical base/markup as that
// charge, so the Finance tab, payout and breakdown modal all show the same numbers.
//   - fixed markup : whole-booking amount; charges/refunds carry NO markup -> markup 0
//   - percentage   : derivedBase = amount/(1+v%) ; markup = ceil(round(base×v%)/5)×5
export const decomposeStandaloneVipAmount = (amount, clientMarkup) => {
  const amt = Math.abs(parseFloat(amount) || 0)
  let markup = 0
  if (clientMarkup && clientMarkup.type === 'percentage') {
    const pct = parseFloat(clientMarkup.value) || 0
    if (pct > 0) {
      const derivedBase = amt / (1 + pct / 100)
      markup = Math.ceil(Math.round(derivedBase * (pct / 100)) / 5) * 5
    }
  }
  const base = parseFloat((amt - markup).toFixed(2))
  return { base, markup: parseFloat(markup.toFixed(2)) }
}

// Standalone VIP only — decompose a MANUAL (non-change-request) refund amount.
// Unlike CR refunds (which must EXACTLY mirror the ceil-to-5 markup of the CR
// charge they reverse — decomposeStandaloneVipAmount above), a manual refund is
// an arbitrary amount, so its markup share is split PROPORTIONALLY by the
// booking's markup %:  base = amount / (1 + pct/100), markup = amount − base.
//   e.g. refund 10 @ 10% markup → base 9.09, markup 0.91 (not 5/5).
// FIXED markup is a one-shot whole-booking amount → refunds carry no markup.
export const decomposeStandaloneVipManualRefund = (amount, clientMarkup) => {
  const amt = Math.abs(parseFloat(amount) || 0)
  let markup = 0
  if (clientMarkup && clientMarkup.type === 'percentage') {
    const pct = parseFloat(clientMarkup.value) || 0
    if (pct > 0) {
      markup = parseFloat((amt - amt / (1 + pct / 100)).toFixed(2))
    }
  }
  const base = parseFloat((amt - markup).toFixed(2))
  return { base, markup }
}

// ─────────────────────────────────────────────────────────────────────────────
// Standalone VIP pricing — SINGLE SOURCE OF TRUTH for the client-side breakdown.
//
// Model (standalone VIP only):
//   • commission = agent earning ONLY — never part of the client price
//   • markup IS added to the client price
//   • markup XOR discount, markup XOR promo code (mutually exclusive)
//   • promo types: percentDiscComm / fixedDiscComm behave as COMMISSION when the
//     booking bills on commission, and as DISCOUNT when it bills on discount;
//     percentageDiscount / fixedDiscount always behave as DISCOUNT.
//   • FIXED promos are one-shot on the base booking — never re-applied to CR
//     deltas (those fall back to the benefits-derived rate).
// This mirrors the backend CR-charge resolution (rolzo-api booking.js
// ~26940-27039) exactly — keep the two in sync.
// ─────────────────────────────────────────────────────────────────────────────

// Resolve the EFFECTIVE billing weights of a standalone VIP booking, promo-aware.
// Accepts either the raw Mongo doc (nested bookingTimeRateInfo) or the client
// API shape (benefits / promoCode / clientMarkup at top level).
// Returns { hasMarkup, discountWeight, commissionWeight, fixedDiscount,
//           fixedCommission, promo } — all numbers, 0 when not applicable.
export const resolveStandaloneVipEffectiveWeights = booking => {
  const out = {
    hasMarkup: false,
    discountWeight: 0,
    commissionWeight: 0,
    fixedDiscount: 0,
    fixedCommission: 0,
    promo: null,
  }
  if (!booking) return out

  const rateInfo = booking.bookingTimeRateInfo || {}
  const benefits = rateInfo.benefits || booking.benefits || null
  const memberBenefits = rateInfo.memberBenefits || booking.memberBenefits
  const promo = rateInfo.promoCode || booking.promoCode || null

  out.hasMarkup = !!booking.clientMarkup
  out.promo = promo && Object.keys(promo).length ? promo : null

  // Benefits / member-benefits base resolution (backend booking.js 26947-26965)
  if (memberBenefits && memberBenefits.list && memberBenefits.list.length) {
    if (
      parseFloat(memberBenefits.discountWeight) >= 1 &&
      benefits &&
      parseFloat(benefits.discountWeight) >= 1
    ) {
      out.discountWeight = parseFloat(memberBenefits.discountWeight)
    }
    if (parseFloat(memberBenefits.commissionWeight) >= 1) {
      out.commissionWeight = parseFloat(memberBenefits.commissionWeight)
    }
  } else if (benefits) {
    if (parseFloat(benefits.discountWeight) >= 1) {
      out.discountWeight = parseFloat(benefits.discountWeight)
    }
    if (parseFloat(benefits.commissionWeight) >= 1) {
      out.commissionWeight = parseFloat(benefits.commissionWeight)
    }
  }

  // Promo override (backend booking.js 26991-27039)
  if (out.promo) {
    const pctWeight =
      parseFloat(out.promo.discountWeight) > 0
        ? parseFloat(out.promo.discountWeight)
        : 0
    const fixedVal =
      parseFloat(out.promo.discountedValue) > 0
        ? parseFloat(out.promo.discountedValue)
        : 0
    const onDiscount = benefits && parseFloat(benefits.discountWeight) > 0
    const isPercentPromo =
      out.promo.type === 'percentDiscComm' ||
      out.promo.type === 'percentageDiscount'
    const isFixedPromo =
      out.promo.type === 'fixedDiscComm' || out.promo.type === 'fixedDiscount'
    const isCommissionBillingPromo =
      (out.promo.type === 'percentDiscComm' ||
        out.promo.type === 'fixedDiscComm') &&
      !onDiscount
    const isDiscountBillingPromo =
      out.promo.type === 'percentageDiscount' ||
      out.promo.type === 'fixedDiscount' ||
      ((out.promo.type === 'percentDiscComm' ||
        out.promo.type === 'fixedDiscComm') &&
        onDiscount)

    if (pctWeight > 0 || fixedVal > 0) {
      if (isCommissionBillingPromo) {
        if (isPercentPromo && pctWeight > 0) {
          out.commissionWeight = pctWeight
          out.discountWeight = 0
        } else if (isFixedPromo && fixedVal > 0) {
          // one-shot fixed commission on the base booking
          out.fixedCommission = fixedVal
        }
      } else if (isDiscountBillingPromo) {
        if (isPercentPromo && pctWeight > 0) {
          out.discountWeight = pctWeight
          out.commissionWeight = 0
        } else if (isFixedPromo && fixedVal > 0) {
          // one-shot fixed discount on the base booking
          out.fixedDiscount = fixedVal
        }
      }
    }
  }

  // markup XOR discount — a markup booking carries no price discount
  if (out.hasMarkup) {
    out.discountWeight = 0
    out.fixedDiscount = 0
  }
  return out
}

// Pre-markup AND pre-discount (gross) base of a single CR charge item.
// bookingTimeRateInfo.actualRate is gross: it grows by the CR's GROSS delta.
//   markup item   → baseAmount strips the markup (amount = base + markup)
//   discount item → amount/baseAmount are NET of the item's own discount %,
//                   so gross up by /(1 − d%) (markup XOR discount: never both)
export const getStandaloneVipCrItemGrossBase = item => {
  if (!item) return 0
  const net =
    item.baseAmount !== undefined && item.baseAmount !== null
      ? parseFloat(item.baseAmount) || 0
      : parseFloat(item.amount) || 0
  const d =
    item.applyDiscount && item.discount
      ? parseFloat(String(item.discount).replace('%', '')) || 0
      : 0
  return d > 0 && d < 100 ? net / (1 - d / 100) : net
}

// Net CR charges / CR refunds out of bookingTimeRateInfo.actualRate in GROSS
// terms, so `actualRate − crChargesGross + crRefundsGross` reconstructs the
// ORIGINAL pre-CR service gross. Also isolates the markup carried by those
// separate CR rows (markup bookings only).
export const getStandaloneVipCrNetting = booking => {
  const out = {
    crChargesGross: 0,
    crRefundsGross: 0,
    crChargesMarkup: 0,
    crRefundsMarkup: 0,
  }
  if (!booking) return out
  // The discount gross-up is a STANDALONE-VIP-ONLY correction. For any other
  // booking (incl. legacy add-on VIP) this reproduces the original netting
  // byte-for-byte: baseAmount ?? amount for charges, decomposed base for
  // refunds — no discount transform.
  const _standalone = booking.isStandaloneVip === true
  const eff = _standalone ? resolveStandaloneVipEffectiveWeights(booking) : null

  if (Array.isArray(booking.charges)) {
    booking.charges
      .filter(c => c && c.isClient)
      .forEach(charge => {
        ;(charge.items || [])
          .filter(i => i && i.isChangeRequest)
          .forEach(i => {
            out.crChargesGross += _standalone
              ? getStandaloneVipCrItemGrossBase(i)
              : i.baseAmount !== undefined && i.baseAmount !== null
              ? parseFloat(i.baseAmount) || 0
              : parseFloat(i.amount) || 0
            if (i.baseAmount !== undefined && i.baseAmount !== null) {
              out.crChargesMarkup += Math.max(
                0,
                (parseFloat(i.amount) || 0) - (parseFloat(i.baseAmount) || 0)
              )
            }
          })
      })
  }

  const hist =
    booking.refund && Array.isArray(booking.refund.refundHistory)
      ? booking.refund.refundHistory
      : []
  hist
    .filter(r => r && r.isChangeRequest)
    .forEach(r => {
      // CR refund amount is the NET client money returned. Strip markup
      // (markup bookings), then gross up by the effective discount weight
      // (discount bookings) — mutually exclusive, only one transform fires.
      const { base, markup } = decomposeStandaloneVipAmount(
        r.amount,
        booking.clientMarkup
      )
      const gross =
        _standalone && eff.discountWeight > 0 && eff.discountWeight < 100
          ? base / (1 - eff.discountWeight / 100)
          : base
      out.crRefundsGross += gross
      out.crRefundsMarkup += markup
    })

  return out
}

export const getMeetAndGreetLegCrRefund = (leg, clientMarkup) => {
  const out = { base: 0, markup: 0, amount: 0 }
  const hist =
    leg && leg.refund && Array.isArray(leg.refund.refundHistory)
      ? leg.refund.refundHistory
      : []
  const crAmount = hist
    .filter(r => r && r.isChangeRequest)
    .reduce((sum, r) => sum + (Math.abs(parseFloat(r.amount)) || 0), 0)
  if (!crAmount) return out

  const { base, markup } = decomposeStandaloneVipAmount(crAmount, clientMarkup)
  const legBase = parseFloat(leg.baseRate) || parseFloat(leg.oldRate) || 0
  const legDiscount = parseFloat(leg.discount) || 0
  const legDiscRate = legBase > 0 ? legDiscount / legBase : 0

  out.base =
    legDiscRate > 0 && legDiscRate < 1 ? base / (1 - legDiscRate) : base
  out.markup = markup
  out.amount = crAmount
  return out
}

// Full client-facing price breakdown for a standalone VIP booking — grand-total
// scope (main service incl. all confirmed CRs + separate non-CR charges − manual
// refunds). Reads the LIVE bookingTimeRateInfo state instead of re-deriving it,
// so it cannot drift from what the client was actually charged.
// Returns { total, serviceGross, discountValue, discountWeightLabel,
//           markupValue, commissionValue, commissionWeightLabel } or null when
// the booking is not standalone VIP / lacks rate info.
// Standalone VIP Transit — the purchased extra greeter hours INSIDE the booking's
// stored price. Mirrors rolzo-api getAdditionalHoursInPrice
// (imports/api/vipRates/additionalHours.js). vipRate.additionalHours /
// additionalHoursAmount are always the CURRENT allowance; after a change on a PAID
// booking the difference is billed on its own charge row (increase) or refund entry
// (decrease) and vipRate.additionalHoursInPrice / additionalHoursAmountInPrice hold
// the part still inside the price. Every MONEY reader must use this, or those hours
// are counted twice. Absent on every other booking → falls back to the current
// fields, so they read exactly as before. amount = NET (charged); gross = hours ×
// the undiscounted per-hour rate (the same basis as actualRate).
export const getStandaloneVipAdditionalHoursInPrice = booking => {
  const vipRate = (booking && booking.vipRate) || {}
  const isTracked =
    vipRate.additionalHoursInPrice !== undefined &&
    vipRate.additionalHoursInPrice !== null
  const hours = isTracked
    ? parseInt(vipRate.additionalHoursInPrice, 10) || 0
    : parseInt(vipRate.additionalHours, 10) || 0
  if (!(hours > 0)) return { hours: 0, amount: 0, gross: 0, isTracked }
  const amount = isTracked
    ? parseFloat(vipRate.additionalHoursAmountInPrice) || 0
    : parseFloat(vipRate.additionalHoursAmount) || 0
  const grossPerHour =
    parseFloat(vipRate.sellingRatePerHourBase) ||
    parseFloat(vipRate.additionalHoursRate) ||
    0
  const gross = parseFloat((hours * grossPerHour).toFixed(2)) || amount
  return { hours, amount, gross, isTracked }
}

/**
 * What the client pays, INSIDE the stored price, for the purchased Extended
 * greeter assistance hours on a saved standalone VIP booking: their charged
 * (post-discount) amount plus their OWN markup (a percentage markup applied to
 * them separately; a fixed markup is the VIP's alone). Hours bought after
 * payment live on their own charge rows and are not part of it.
 *
 * A change request only re-prices the VIP service, so its new total must carry
 * this unchanged — otherwise "new VIP total − stored price" reads the hours as a
 * refund (VIP 330 + hours 220: a luggage change came out at −220).
 */
export const getStandaloneVipHoursInPriceCharged = booking => {
  if (!booking || booking.isStandaloneVip !== true) return 0
  const inPrice = getStandaloneVipAdditionalHoursInPrice(booking)
  if (!(inPrice.hours > 0)) return 0
  const markup = booking.clientMarkup
  const pct =
    markup && markup.type === 'percentage' ? parseFloat(markup.value) || 0 : 0
  const hoursMarkup =
    pct > 0 && resolveStandaloneVipEffectiveWeights(booking).hasMarkup
      ? Math.min(
          parseFloat(booking.clientMarkupRate) || 0,
          Math.ceil(Math.round((inPrice.gross * pct) / 100) / 5) * 5
        )
      : 0
  return parseFloat(((inPrice.amount || 0) + hoursMarkup).toFixed(2))
}

/**
 * Extended greeter assistance hours that were bought BEFORE payment (inside the
 * paid price) and later removed and refunded from that main payment. Each such
 * refund lowers the stored price AND leaves a refund entry, so a screen that
 * starts from the stored price and then subtracts the refund counts the hour
 * twice (paid 481.50: VIP 360 + 1h 121.50 → "360 − 121.50 = 238.50"). Adding
 * these back rebuilds what was actually paid; the refund row takes them off once.
 * Charge-backed hours refund on their own charge row and are not included.
 *
 * Mirrors rolzo-api additionalHours.js getRefundedInPriceHours: entries flagged
 * `priceReduced` (written after the price was lowered) count in full; older,
 * unflagged entries are inferred — hours the ledgers still hold (in-price + live
 * charge-backed hours, a charge refunded from the charges section unwinding its
 * share) above the current allowance never left the price.
 * @returns {{ hours, amount, base, markup, gross, still }} out-of-price sums,
 *   plus `still` (refunded hours that are still inside the price)
 */
export const getStandaloneVipRefundedInPriceHours = booking => {
  const zero = () => ({ hours: 0, amount: 0, base: 0, markup: 0, gross: 0 })
  const out = zero()
  const still = zero()
  if (!booking || booking.isStandaloneVip !== true) return { ...out, still }
  const history = (booking.refund && booking.refund.refundHistory) || []
  const add = (acc, entry, f = 1) => {
    const amount = parseFloat(entry.amount) || 0
    const markup = parseFloat(entry.markupAmount) || 0
    const base =
      entry.baseAmount !== undefined && entry.baseAmount !== null
        ? parseFloat(entry.baseAmount) || 0
        : amount - markup
    acc.hours += (parseInt(entry.additionalHoursDelta, 10) || 0) * f
    acc.amount += amount * f
    acc.base += base * f
    acc.markup += markup * f
    acc.gross += (parseFloat(entry.grossAmount) || base) * f
    return acc
  }
  const unflagged = zero()
  history.forEach(entry => {
    if (!entry || entry.isAdditionalHours !== true) return
    if (entry.priceReduced === true) add(out, entry)
    else add(unflagged, entry)
  })
  if (unflagged.hours > 0) {
    const allowance =
      parseInt(booking.vipRate && booking.vipRate.additionalHours, 10) || 0
    const inPriceHours = getStandaloneVipAdditionalHoursInPrice(booking).hours
    // Same per-charge rule as rolzo-api deriveAdditionalHoursFromLedgers.
    const liveChargeHours = (booking.charges || []).reduce((total, charge) => {
      if (!charge) return total
      const items = Array.isArray(charge.items) ? charge.items : []
      const item = items.find(i => i && i.isAdditionalHours)
      if (!item) return total
      const bought = parseInt(item.additionalHoursDelta, 10) || 0
      if (!(bought > 0)) return total
      const rowTotal = items.reduce(
        (a, i) => a + (parseFloat(i.amount) || 0),
        0
      )
      const refunded = parseFloat(charge.refund && charge.refund.amount) || 0
      const marked =
        parseInt(charge.refund && charge.refund.additionalHoursRefunded, 10) ||
        0
      const implied =
        rowTotal > 0 && refunded > 0
          ? Math.min(bought, Math.floor((refunded / rowTotal) * bought + 1e-9))
          : 0
      return total + Math.max(0, bought - Math.max(marked, implied))
    }, 0)
    const stillHours = Math.min(
      unflagged.hours,
      Math.max(0, inPriceHours + liveChargeHours - allowance)
    )
    const f = (unflagged.hours - stillHours) / unflagged.hours
    ;['hours', 'amount', 'base', 'markup', 'gross'].forEach(k => {
      out[k] += unflagged[k] * f
      still[k] += unflagged[k] * (1 - f)
    })
  }
  return { ...out, still }
}

/**
 * Whether a saved standalone VIP booking's purchased hours sit OUTSIDE
 * actualRate (`additive`: figures built from actualRate must add them) — judged
 * on the pair AS PAID. A removal lowers the stored price and in-price hours but
 * never actualRate, so on an hours-included row the post-removal pair stops
 * reconciling and the plain check flips to "outside", adding hours actualRate
 * already carries. Putting the refunded hours back into the price, markup and
 * in-price amount first restores the charged pair (same as FinanceTabV2 and
 * rolzo-api isAdditionalHoursIncludedInActualRateAsPaid).
 * @returns {{ additive: boolean, inPrice, refunded }}
 */
export const getStandaloneVipHoursAsPaid = booking => {
  const refunded = getStandaloneVipRefundedInPriceHours(booking)
  const inPrice = getStandaloneVipAdditionalHoursInPrice(booking)
  if (!booking || !(inPrice.hours > 0 || refunded.hours > 0)) {
    return { additive: true, inPrice, refunded }
  }
  const rateInfo = booking.bookingTimeRateInfo || {}
  const actual = parseFloat(rateInfo.actualRate)
  const eff = resolveStandaloneVipEffectiveWeights(booking)
  const benefits = rateInfo.benefits || booking.benefits || null
  let discount = 0
  if (!eff.hasMarkup) {
    if (eff.fixedDiscount > 0) discount = eff.fixedDiscount
    else if (eff.discountWeight > 0)
      discount = (actual * eff.discountWeight) / 100
    else if (parseFloat(benefits?.discountValue) > 0)
      discount = parseFloat(benefits.discountValue)
  }
  const markup = eff.hasMarkup
    ? (parseFloat(booking.clientMarkupRate) || 0) + refunded.markup
    : 0
  const price = rateInfo.price ?? booking.confirmTimeRateInfo?.price
  const addOn = resolveStandaloneVipHoursAddOn({
    chargedBase: actual - discount + markup,
    hoursAmount: inPrice.amount + refunded.base,
    price:
      price !== undefined && price !== null
        ? (parseFloat(price) || 0) + refunded.amount
        : price,
  })
  return { additive: addOn > 0, inPrice, refunded }
}

/** "Refund (1 hour of extended greeter assistance removed)" for such an entry. */
export const getStandaloneVipHoursRefundLabel = (
  entry,
  fallback = 'Refund'
) => {
  if (!entry || entry.isAdditionalHours !== true) return fallback
  const hours = parseInt(entry.additionalHoursDelta, 10) || 0
  if (!(hours > 0)) return fallback
  return `${fallback} (${hours} hour${
    hours === 1 ? '' : 's'
  } of extended greeter assistance removed)`
}

/**
 * The purchased Extended greeter assistance hours inside a saved standalone
 * VIP booking's price, for the net / commission switch preview:
 * { hours, gross } with gross the UNDISCOUNTED hours amount. The preview starts
 * from bookingTimeRateInfo.actualRate, so it returns 0 hours when that snapshot
 * already has the hours folded in (the two-shapes case) — they would otherwise
 * be counted twice.
 */
export const getStandaloneVipSwitchPreviewHours = booking => {
  if (!booking || booking.isStandaloneVip !== true) return null
  const inPrice = getStandaloneVipAdditionalHoursInPrice(booking)
  if (!(inPrice.hours > 0) || !(inPrice.gross > 0)) return null
  const rateInfo = booking.bookingTimeRateInfo || {}
  const actual = parseFloat(rateInfo.actualRate)
  const benefits = rateInfo.benefits || {}
  const dw = parseFloat(benefits.discountWeight) || 0
  // clientMarkupRate is the COMBINED markup (VIP + hours), so base + the
  // in-price hours amount lands on price whenever the hours are outside.
  const markup = dw > 0 ? 0 : parseFloat(rateInfo.clientMarkupRate) || 0
  const chargedBase = Number.isFinite(actual)
    ? actual - (actual * dw) / 100 + markup
    : NaN
  const addOn = resolveStandaloneVipHoursAddOn({
    chargedBase,
    hoursAmount: inPrice.amount,
    price: rateInfo.price,
  })
  return addOn > 0 ? { hours: inPrice.hours, gross: inPrice.gross } : null
}

/**
 * What ONE additional greeter hour costs the client on a saved standalone VIP
 * booking: the stored per-hour rate plus the hours' own markup. A percentage
 * markup is applied to the hours separately from the VIP service (nearest-five
 * on its own, the rule the booking flow priced them with); a fixed markup is
 * the VIP's alone, and markup never applies under a discount. Matches the
 * "per additional hour" price the client saw on the hours card.
 */
export const getStandaloneVipClientPerHourRate = booking => {
  const perHour = parseFloat(booking?.vipRate?.sellingRatePerHour) || 0
  if (!(perHour > 0) || booking?.isStandaloneVip !== true) return perHour
  const markup = booking.clientMarkup
  const pct =
    markup && markup.type === 'percentage' ? parseFloat(markup.value) || 0 : 0
  if (!(pct > 0) || !resolveStandaloneVipEffectiveWeights(booking).hasMarkup) {
    return perHour
  }
  return perHour + Math.ceil(Math.round((perHour * pct) / 100) / 5) * 5
}

/**
 * How much of the purchased-hours amount still has to be ADDED to a total that
 * was derived from `actualRate`.
 *
 * `actualRate` is NOT consistent across bookings: on some standalone VIP Transit
 * rows it is the base service alone, on others the purchased greeter hours are
 * already folded into it. Verified on staging — 5449-V and 9651-R were created
 * the same day, same net mode, same 5% discount, no markup/promo/Prive, and
 * disagree. Anything that unconditionally does `actualRate + hoursAmount`
 * therefore double-counts the hours on half the rows (5449-V read 42.75 for a
 * 23.75 booking; 9650-R read 890.00 for a 690.00 one).
 *
 * Rather than guess the shape, ask the AUTHORITATIVE figure: `price` is what was
 * actually charged, and it is correct on every row regardless of shape. Whichever
 * of `charged` / `charged + hours` lands on it is the truth.
 *
 * @param {number} chargedBase what the booking's stored price-side rate resolves
 *   to from actualRate (discount/markup already applied)
 * @param {number} hoursAmount the in-price purchased-hours amount
 * @param {number} price bookingTimeRateInfo.price — the charged total
 * @returns {number} `hoursAmount` when they must be added, 0 when already inside
 */
export const resolveStandaloneVipHoursAddOn = ({
  chargedBase,
  hoursAmount,
  price,
} = {}) => {
  const hours = parseFloat(hoursAmount) || 0
  if (!(hours > 0)) return 0
  const base = parseFloat(chargedBase)
  const charged = parseFloat(price)
  // No usable price to compare against (a draft, or a row missing the field):
  // fall back to ADDING, which is the long-standing behaviour and the only one
  // that is right when actualRate genuinely excludes the hours.
  if (!Number.isFinite(base) || !Number.isFinite(charged) || charged === 0) {
    return hours
  }
  // Whole cents: stored prices are built with toFixed(2) in places, and a raw
  // comparison would let sub-cent residue pick the wrong branch.
  const cents = v => Math.round(v * 100)
  const fitIncluded = Math.abs(cents(base) - cents(charged))
  const fitExcluded = Math.abs(cents(base + hours) - cents(charged))
  // Deliberately asymmetric. Returning 0 wrongly DROPS the hours from a booking
  // that displays correctly today, so "already inside" has to reconstruct the
  // charged price (near-)exactly AND beat the alternative. Anything else — a
  // pricing effect the caller's chargedBase does not model (an unusual promo, a
  // CR that moved the price but not the rate), a stale in-price amount, a tie —
  // falls through to ADDING, the long-standing behaviour.
  // Two cents of slack: stored amounts are rounded to 2dp, so an exact
  // reconstruction can still land a cent out.
  return fitIncluded <= 2 && fitIncluded < fitExcluded ? 0 : hours
}

export const getStandaloneVipPriceBreakdown = booking => {
  if (!booking || booking.isStandaloneVip !== true) return null
  const rateInfo = booking.bookingTimeRateInfo || {}
  const gross = parseFloat(
    rateInfo.actualRate !== undefined ? rateInfo.actualRate : booking.actualRate
  )
  if (!gross || isNaN(gross)) return null

  const eff = resolveStandaloneVipEffectiveWeights(booking)
  const benefits = rateInfo.benefits || booking.benefits || null

  // ── main service (actualRate is LIVE: includes every confirmed CR delta) ──
  let markupValue = eff.hasMarkup
    ? parseFloat(booking.clientMarkupRate) || 0
    : 0
  let discountValue = 0
  if (!eff.hasMarkup) {
    if (eff.fixedDiscount > 0) {
      discountValue = eff.fixedDiscount
    } else if (eff.discountWeight > 0) {
      discountValue = (gross * eff.discountWeight) / 100
    } else if (benefits && parseFloat(benefits.discountValue) > 0) {
      discountValue = parseFloat(benefits.discountValue)
    }
  }
  let commissionValue =
    eff.fixedCommission > 0
      ? eff.fixedCommission
      : calculateCommissionValue(
          gross,
          eff.commissionWeight,
          eff.discountWeight,
          booking?.pickUpLocation?.country
        )
  let total = gross - discountValue + markupValue

  // ── purchased extra greeter hours inside the price (Transit). actualRate never
  //    includes them: the charged (net) amount joins the total, commission is
  //    earned on their gross, and their own discount share is gross − net. The
  //    markup on them is already inside the live clientMarkupRate above. ──
  const hoursInPrice = getStandaloneVipAdditionalHoursInPrice(booking)
  // Only fold the hours in when they are NOT already inside actualRate — the
  // shape is inconsistent across bookings (see resolveStandaloneVipHoursAddOn),
  // and adding them unconditionally read 42.75 on a 23.75 booking. When they are
  // already inside, `gross` above carried them, so the discount and commission
  // derived from it carried them too and must not be topped up either.
  // Judged AS PAID (getStandaloneVipHoursAsPaid): after a paid removal the
  // stored price no longer reconciles with actualRate on its own.
  const {
    additive: hoursAdditive,
    refunded: _refundedHours,
  } = getStandaloneVipHoursAsPaid(booking)
  if (hoursAdditive && hoursInPrice.amount > 0) {
    total += hoursInPrice.amount
    if (!eff.hasMarkup) {
      discountValue += Math.max(0, hoursInPrice.gross - hoursInPrice.amount)
    }
    if (!(eff.fixedCommission > 0)) {
      commissionValue += calculateCommissionValue(
        hoursInPrice.gross,
        eff.commissionWeight,
        eff.discountWeight,
        booking?.pickUpLocation?.country
      )
    }
  }

  // ── in-price hours removed and refunded from the main payment. Their removal
  //    took them out of the stored price (and markup), while the refund loop
  //    below subtracts their refund again — so they are put back here (what the
  //    main payment covered), exactly as that loop expects of any other refund.
  //    When the hours sit inside actualRate, `gross` above still carries them
  //    and only the markup their removal took off has to come back. ──
  if (_refundedHours.amount > 0) {
    if (hoursAdditive) {
      total += _refundedHours.amount
      if (eff.hasMarkup) {
        markupValue += _refundedHours.markup
      } else {
        discountValue += Math.max(0, _refundedHours.gross - _refundedHours.base)
      }
      if (!(eff.fixedCommission > 0)) {
        commissionValue += calculateCommissionValue(
          _refundedHours.gross,
          eff.commissionWeight,
          eff.discountWeight,
          booking?.pickUpLocation?.country
        )
      }
    } else if (eff.hasMarkup) {
      markupValue += _refundedHours.markup
      total += _refundedHours.markup
    }
  }

  // The hours' OWN share of the discount and commission, for the breakdown
  // modal's "Extended greeter assistance" sub-lines. Computed for DISPLAY
  // whether or not the fold above ran: when the hours are already inside
  // actualRate the aggregates picked these up through `gross` instead, but the
  // modal still renders the row and still owes the client the detail under it.
  const hoursDiscountValue =
    hoursInPrice.hours > 0 && !eff.hasMarkup
      ? Math.max(0, hoursInPrice.gross - hoursInPrice.amount)
      : 0
  const hoursCommissionValue =
    hoursInPrice.hours > 0 && !(eff.fixedCommission > 0)
      ? calculateCommissionValue(
          hoursInPrice.gross,
          eff.commissionWeight,
          eff.discountWeight,
          booking?.pickUpLocation?.country
        )
      : 0

  // The hours' OWN markup. A percentage markup is applied to the hours
  // separately from the VIP service (nearestFive(hours gross × pct), the same
  // rule the booking flow charged them with); a fixed markup is the VIP's
  // alone. The stored clientMarkupRate is the whole of it (VIP + hours), so
  // this is only ever a share of markupValue — clamped so it can never claim
  // more than was actually charged.
  const _hoursMarkupConfig = booking.clientMarkup
  const _hoursMarkupPct =
    _hoursMarkupConfig && _hoursMarkupConfig.type === 'percentage'
      ? parseFloat(_hoursMarkupConfig.value) || 0
      : 0
  const hoursMarkupValue =
    eff.hasMarkup && hoursInPrice.hours > 0 && _hoursMarkupPct > 0
      ? Math.min(
          Math.max(0, markupValue),
          Math.ceil(
            Math.round((hoursInPrice.gross * _hoursMarkupPct) / 100) / 5
          ) * 5
        )
      : 0

  // Charge-only portions, tracked alongside the running aggregates below.
  // `total` / `markupValue` / `discountValue` must keep aggregating everything
  // (the "Your earnings" section is a whole-booking figure), but the SERVICE
  // section of the price breakdown has to render without the charges or they
  // are shown once inside the service line and again as their own rows — the
  // grand total then double-counts them.
  let chargesTotalAdded = 0
  let chargesMarkupAdded = 0
  let chargesDiscountAdded = 0
  let chargesCommissionAdded = 0

  // ── separate (non-CR) additional charges — billed on top of the main total ──
  if (Array.isArray(booking.charges)) {
    booking.charges
      .filter(c => c && c.isClient)
      .forEach(charge => {
        // CR charges are already inside actualRate/price — but a CHARGE-LEVEL
        // refund on a CR charge (admin refunds that specific charge) is NOT
        // reflected in the live price, so subtract it here and claw back the
        // charge's own markup / discount / commission proportionally
        // (refundShare = refund / charge total — same rule as the Finance tab
        // chargeNetRatio and the payout statement refundShare).
        const crItems = (charge.items || []).filter(i => i && i.isChangeRequest)
        if (crItems.length && charge.refund && charge.refund.amount) {
          const crTotal = crItems.reduce(
            (s, i) => s + (parseFloat(i.amount) || 0),
            0
          )
          const refAmt = Math.abs(parseFloat(charge.refund.amount) || 0)
          if (refAmt > 0 && crTotal > 0) {
            const share = Math.min(1, refAmt / crTotal)
            total -= refAmt
            crItems.forEach(i => {
              const amt = parseFloat(i.amount) || 0
              if (i.baseAmount !== undefined && i.baseAmount !== null) {
                markupValue -=
                  Math.max(0, amt - (parseFloat(i.baseAmount) || 0)) * share
              }
              const d =
                i.applyDiscount && i.discount
                  ? parseFloat(String(i.discount).replace('%', '')) || 0
                  : 0
              if (d > 0 && d < 100) {
                discountValue -= (amt / (1 - d / 100) - amt) * share
              }
              if (i.applyCommission) {
                const itemCommW =
                  i.commission != null &&
                  parseFloat(String(i.commission).replace('%', '')) > 0
                    ? parseFloat(String(i.commission).replace('%', ''))
                    : eff.commissionWeight
                const cBase =
                  i.baseAmount !== undefined && i.baseAmount !== null
                    ? parseFloat(i.baseAmount) || 0
                    : amt
                const cGross = d > 0 && d < 100 ? cBase / (1 - d / 100) : cBase
                commissionValue -=
                  calculateCommissionValue(
                    cGross,
                    itemCommW,
                    0,
                    booking?.pickUpLocation?.country
                  ) * share
              }
            })
          }
        }

        const items = (charge.items || []).filter(i => i && !i.isChangeRequest)
        if (!items.length) return
        const chargeAmountTotal = items.reduce(
          (s, i) => s + (parseFloat(i.amount) || 0),
          0
        )
        const chargeRefundAmt =
          charge.refund && charge.refund.amount
            ? parseFloat(charge.refund.amount) || 0
            : 0
        const ratio =
          chargeRefundAmt > 0 && chargeAmountTotal > 0
            ? Math.max(
                0,
                (chargeAmountTotal - chargeRefundAmt) / chargeAmountTotal
              )
            : 1
        total += chargeAmountTotal - chargeRefundAmt
        chargesTotalAdded += chargeAmountTotal - chargeRefundAmt
        items.forEach(i => {
          const amt = parseFloat(i.amount) || 0
          // discount carried by the item (its own stored %)
          const d =
            i.applyDiscount && i.discount
              ? parseFloat(String(i.discount).replace('%', '')) || 0
              : 0
          const grossItem = d > 0 && d < 100 ? amt / (1 - d / 100) : amt
          if (d > 0) {
            discountValue += (grossItem - amt) * ratio
            chargesDiscountAdded += (grossItem - amt) * ratio
          }
          // markup carried by the item
          if (i.baseAmount !== undefined && i.baseAmount !== null) {
            const itemMarkup =
              Math.max(0, amt - (parseFloat(i.baseAmount) || 0)) * ratio
            markupValue += itemMarkup
            chargesMarkupAdded += itemMarkup
          }
          // commission carried by the item (its own stored % first)
          if (i.applyCommission) {
            const itemCommW =
              i.commission != null &&
              parseFloat(String(i.commission).replace('%', '')) > 0
                ? parseFloat(String(i.commission).replace('%', ''))
                : eff.commissionWeight
            // Extra greeter hours charge: commission on its pre-markup base, the
            // same base the payout statement uses (never the markup itself).
            const hoursBase =
              i.isAdditionalHours === true &&
              i.baseAmount !== undefined &&
              i.baseAmount !== null
                ? parseFloat(i.baseAmount) || 0
                : null
            const commGross =
              hoursBase === null
                ? grossItem
                : d > 0 && d < 100
                ? hoursBase / (1 - d / 100)
                : hoursBase
            const itemCommission =
              calculateCommissionValue(
                commGross,
                itemCommW,
                0,
                booking?.pickUpLocation?.country
              ) * ratio
            commissionValue += itemCommission
            chargesCommissionAdded += itemCommission
          }
        })
      })
  }

  // ── manual (non-CR) booking-level refunds — CR refunds are already inside
  //    actualRate/price, so only manual ones reduce the grand total here ──
  const hist =
    booking.refund && Array.isArray(booking.refund.refundHistory)
      ? booking.refund.refundHistory
      : null
  const manualRefunds = hist
    ? hist.filter(r => r && !r.isChangeRequest)
    : booking.refund && parseFloat(booking.refund.amount) > 0
    ? [{ amount: booking.refund.amount }]
    : []
  manualRefunds.forEach(r => {
    const amt = Math.abs(parseFloat(r.amount) || 0)
    if (!amt) return
    total -= amt
    // Manual refunds split PROPORTIONALLY (not the ceil-to-5 CR mirror rule).
    // Extra greeter hours refunds carry their exact split.
    const { base, markup } =
      r.isAdditionalHours === true &&
      r.baseAmount !== undefined &&
      r.baseAmount !== null
        ? {
            base: parseFloat(r.baseAmount) || 0,
            markup: parseFloat(r.markupAmount) || 0,
          }
        : decomposeStandaloneVipManualRefund(amt, booking.clientMarkup)
    if (eff.hasMarkup && markup > 0) markupValue -= markup
    if (eff.discountWeight > 0 && eff.discountWeight < 100) {
      discountValue -= amt / (1 - eff.discountWeight / 100) - amt
    }
    // commission clawback — same rule as the Finance tab refund breakdown.
    // An hours entry's base is NET of the discount, and calculateCommissionValue
    // applies the discount weight itself, so claw back on its pre-discount
    // gross — the basis the hours earned commission on.
    const commissionBase =
      r.isAdditionalHours === true
        ? parseFloat(r.grossAmount) ||
          (eff.discountWeight > 0 && eff.discountWeight < 100
            ? base / (1 - eff.discountWeight / 100)
            : base)
        : base
    if (eff.commissionWeight > 0) {
      commissionValue -= calculateCommissionValue(
        commissionBase,
        eff.commissionWeight,
        eff.discountWeight,
        booking?.pickUpLocation?.country
      )
    }
  })

  const round2 = v => parseFloat(Math.max(0, v).toFixed(2))
  return {
    total: round2(total),
    // The same figures with the separate additional charges taken back out, so
    // the breakdown's SERVICE section can render the service alone while the
    // charges render as their own rows.
    serviceTotal: round2(total - chargesTotalAdded),
    serviceMarkupValue: round2(markupValue - chargesMarkupAdded),
    serviceDiscountValue: round2(discountValue - chargesDiscountAdded),
    serviceCommissionValue: round2(commissionValue - chargesCommissionAdded),
    chargesTotal: round2(chargesTotalAdded),
    // Purchased hours as this breakdown actually counted them, so the modal can
    // show them on their own row and take the SAME figure back out of the base.
    hoursInPriceAmount: round2(hoursInPrice.amount),
    hoursInPriceGross: round2(hoursInPrice.gross),
    hoursInPriceHours: hoursInPrice.hours,
    hoursDiscountValue: round2(hoursDiscountValue),
    hoursCommissionValue: round2(hoursCommissionValue),
    hoursMarkupValue: round2(hoursMarkupValue),
    serviceGross: round2(gross),
    discountValue: round2(discountValue),
    markupValue: round2(markupValue),
    commissionValue: round2(commissionValue),
    discountWeightLabel:
      eff.fixedDiscount > 0
        ? null
        : eff.discountWeight > 0
        ? eff.discountWeight
        : null,
    commissionWeightLabel:
      eff.fixedCommission > 0
        ? null
        : eff.commissionWeight > 0
        ? eff.commissionWeight
        : null,
  }
}

export function getVehicleLabelForCR(vehicle) {
  if (!vehicle) return ''
  if (vehicle.label) return vehicle.label
  const data = vehicle.data
  if (data) {
    if (data.label) return data.label
    if (data.brandName && data.modelName) {
      return `${data.brandName} ${data.modelName}`
    }
  }
  if (vehicle.brandName && vehicle.modelName) {
    return `${vehicle.brandName} ${vehicle.modelName}`
  }
  return ''
}

export function calculateCRCostDifference({
  newRate,
  price,
  waitCoverageRate = 0,
  cancelCoverageRate = 0,
  bookingSurge = false,
  bookingRateSourceIds = {},
  itemRateSourceIds = {},
  itemSurge = null,
  isTransferBooking = false,
  isBTHBooking = false,
  pickUpDateAndTimeChanged = false,
  bthDateTimeOnlyChanged = false,
  hasLocationChange = false,
  hasStopsChange = false,
  hasDaysChange = false,
  hasDurationChange = false,
  originalBookingRate = null,
  originalItineraryRate = null,
  oldVehicleLabel = '',
  newVehicleLabel = '',
}) {
  const newRateWithCoverage = newRate + waitCoverageRate + cancelCoverageRate
  let costDifference = parseFloat((newRateWithCoverage - price).toFixed(2))
  let surge = null
  let preserveBuyingForSameRateSource = false

  const sameRateSource =
    (bookingRateSourceIds.globalRateItemId &&
      itemRateSourceIds.globalRateItemId ===
        bookingRateSourceIds.globalRateItemId) ||
    (bookingRateSourceIds.smartRateId &&
      itemRateSourceIds.smartRateId === bookingRateSourceIds.smartRateId)

  const hasSurgeOverride =
    itemSurge &&
    itemSurge.weight &&
    (itemSurge.type === 'event' || !bookingSurge)

  if (isTransferBooking) {
    if (pickUpDateAndTimeChanged) {
      if (hasSurgeOverride) {
        costDifference = parseFloat(
          (price * (itemSurge.weight / 100)).toFixed(2)
        )
        surge = itemSurge
      } else {
        costDifference = 0
      }
    } else if (hasLocationChange && !hasStopsChange && sameRateSource) {
      costDifference = 0
      preserveBuyingForSameRateSource = true
    } else if (
      hasStopsChange &&
      sameRateSource &&
      originalItineraryRate &&
      originalBookingRate
    ) {
      const adjustedNewRate =
        newRate * (originalBookingRate / originalItineraryRate) +
        waitCoverageRate +
        cancelCoverageRate
      costDifference = parseFloat((adjustedNewRate - price).toFixed(2))
    }
  } else if (isBTHBooking) {
    if (bthDateTimeOnlyChanged || pickUpDateAndTimeChanged) {
      if (hasSurgeOverride) {
        costDifference = parseFloat(
          (price * (itemSurge.weight / 100)).toFixed(2)
        )
        surge = itemSurge
      } else {
        costDifference = 0
      }
    } else if (
      hasLocationChange &&
      !hasStopsChange &&
      !hasDaysChange &&
      !hasDurationChange &&
      sameRateSource
    ) {
      costDifference = 0
      preserveBuyingForSameRateSource = true
    } else if (
      hasStopsChange &&
      sameRateSource &&
      originalItineraryRate &&
      originalBookingRate
    ) {
      const adjustedNewRate =
        newRate * (originalBookingRate / originalItineraryRate) +
        waitCoverageRate +
        cancelCoverageRate
      costDifference = parseFloat((adjustedNewRate - price).toFixed(2))
    } else if (
      hasDaysChange &&
      sameRateSource &&
      originalItineraryRate &&
      originalBookingRate
    ) {
      const adjustedNewRate =
        newRate * (originalBookingRate / originalItineraryRate) +
        waitCoverageRate +
        cancelCoverageRate
      costDifference = parseFloat((adjustedNewRate - price).toFixed(2))
    } else if (
      hasDurationChange &&
      sameRateSource &&
      originalItineraryRate &&
      originalBookingRate
    ) {
      const adjustedNewRate =
        newRate * (originalBookingRate / originalItineraryRate) +
        waitCoverageRate +
        cancelCoverageRate
      costDifference = parseFloat((adjustedNewRate - price).toFixed(2))
    }
  } else if (pickUpDateAndTimeChanged) {
    if (costDifference > 0) {
      if (hasSurgeOverride) {
        costDifference = parseFloat(
          (price * (itemSurge.weight / 100)).toFixed(2)
        )
        surge = itemSurge
      } else {
        costDifference = 0
      }
    } else if (costDifference < 0) {
      costDifference = 0
    }
  }

  if (oldVehicleLabel && newVehicleLabel && costDifference !== 0) {
    const equivalentVehiclePairs = {
      'Mercedes-Benz E-Class': 'Mercedes-Benz EQE',
      'Mercedes-Benz EQE': 'Mercedes-Benz E-Class',
      'Mercedes-Benz V-Class': 'Mercedes-Benz EQV',
      'Mercedes-Benz EQV': 'Mercedes-Benz V-Class',
    }
    if (equivalentVehiclePairs[oldVehicleLabel] === newVehicleLabel) {
      costDifference = 0
    }
  }

  return { costDifference, surge, preserveBuyingForSameRateSource }
}
